/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useDebounce } from '@squiz/dxp-console-support';
import { SearchFilter, SearchFilterProps } from '@squiz/sds';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEBOUNCE_DELAY } from '../LogsFilter/LogsFilter.constants';
import { FilterByTextProps } from './FilterByText.props';

/**
 * The logs search bar that allows filtering by text.
 */
export const FilterByText = (props: FilterByTextProps): JSX.Element => {
  const { icon, placeholderText, queryParam } = props;

  // URL search params
  const [searchParams, setSearchParams] = useSearchParams();

  // Text filter value
  const [filterValue, setFilterValue] = useState<string>(searchParams.get(queryParam) as string);
  const debouncedText = useDebounce(filterValue, DEBOUNCE_DELAY);

  // Update text query search param with debounced text
  useEffect(() => {
    if (debouncedText === filterValue) {
      const paramQuery = searchParams.get(queryParam) as string;

      if (paramQuery !== debouncedText) {
        if (debouncedText) {
          searchParams.set(queryParam, debouncedText);
        } else {
          searchParams.delete(queryParam);
        }

        setSearchParams(searchParams);
      }
    }

    // Only update search param if the debounced text has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedText]);

  // Keep text field value in sync with search param
  // Important for navigating forward/back through browser history
  useEffect(() => {
    const paramQuery = searchParams.get(queryParam) as string;

    if (paramQuery !== filterValue) {
      setFilterValue(paramQuery ?? '');
    }

    // Only update the text if the search param changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // The search bar
  const filterProps = useMemo(() => {
    return {
      customIcon: icon,
      filterValue,
      placeholder: placeholderText,
      setFilterValue,
    } as SearchFilterProps;
  }, [filterValue, icon, placeholderText, setFilterValue]);

  return <SearchFilter {...filterProps} />;
};
